import React, { FC, RefObject, useEffect, useMemo, useRef } from 'react'
import { FormikHelpers } from 'formik'

import AssesmentLike from '../../../UI/Icons/linkedin/AssesmentLike'
import AssesmentsClap from '../../../UI/Icons/linkedin/AssesmentsClap'
import AssesmentsHeart from '../../../UI/Icons/linkedin/AssesmentsHeart'
import Comment from '../../../UI/Icons/linkedin/Comment'
import Like from '../../../UI/Icons/linkedin/Like'
import Send from '../../../UI/Icons/linkedin/Send'
import Share from '../../../UI/Icons/linkedin/Share'

import Ellipsis from '../../../UI/Icons/Ellipsis'
import UploadImage from '../../../UploadPostImages'
import UploadVideo from '../../../UploadVideo'
import MessageField from '../UI/MessageField'

import {
  TPostSocialManagePost,
  VIEW_TYPE,
  TFieldChangePayload,
  TRemoveAttachmentPayload,
  TChangeAttachmentExtrafieldPayload
} from '../../../../store/managePost/newTypes'

import { TMediaCallbacks } from '../../../../pages/CreatePost'

import './LinkedinEditor.scss'
import { POST_TYPE, SOCIAL_NETWORK } from '../../../../models/post'
import LinkPreview from '../UI/LinkPreview'
import { TSocialDetails } from '../../../../selectors/ProjectSelector'
import VideoTitleInput from '../UI/VideoTitleInput'
import { getLinkedinMentionsThunk, hashtagSearchThunk } from '../../../../store/managePost/thunks'
import { useDispatch } from '../../../../store'
import { setPostSocialExtrafieldsAction } from '../../../../store/managePost/slice'

interface LinkedinPreviewProps {
  post: TPostSocialManagePost
  onTextChange: (data: TFieldChangePayload) => void
  refMediaCallbaks: RefObject<TMediaCallbacks>
  addMediaToTemporaryLibrary: (data: any) => void
  addLinkToAttachments: (isChildLink: boolean) => void
  deleteMediaFromPostSocial: (data: TRemoveAttachmentPayload) => void
  view: VIEW_TYPE
  restrictions: any
  socialDetails: TSocialDetails
  setExtraField: (data: TChangeAttachmentExtrafieldPayload) => void
  publishDate: string
}

const LinkedinEditor: FC<LinkedinPreviewProps> = ({
  post,
  onTextChange,
  refMediaCallbaks,
  addMediaToTemporaryLibrary,
  addLinkToAttachments,
  restrictions,
  publishDate,
  socialDetails,
  setExtraField,
  deleteMediaFromPostSocial
}) => {
  const dispatch = useDispatch()

  const socialData = useMemo(() => socialDetails[SOCIAL_NETWORK.LINKEDIN_SOCIAL], [])

  useEffect(() => {
    if (post.type === POST_TYPE.POST_TYPE_LINK && !post.attachments.length) {
      addLinkToAttachments(false)
    }
  }, [post.attachments])

  const manageMediaProps = {
    attachments: post.attachments,
    isEditable: post.isEditable,
    addMediaToPost: addMediaToTemporaryLibrary,
    refMediaCallbaks,
    removeMediaFromStore: deleteMediaFromPostSocial,
    restrictions,
    social: SOCIAL_NETWORK.LINKEDIN_SOCIAL
  }


  const getLinkedinMentions = async (query: string) =>
    await dispatch(getLinkedinMentionsThunk(query))

  const getHashtags = async (query: string)=> 
    await dispatch(hashtagSearchThunk({social: SOCIAL_NETWORK.LINKEDIN_SOCIAL,query}))


  const setMentionToStore = (mention: any) => {
    dispatch(setPostSocialExtrafieldsAction({ social: SOCIAL_NETWORK.LINKEDIN_SOCIAL, mention }))
  }

  const attachmentNotToRemove = useMemo(() => {
    return post.attachments.find((attachment) => !attachment.toRemove) || null
  }, [post.attachments])

  return (
    <div className={`linkedin-editor post-editor`}>
      <div className='linkedin-content'>
        <div className='linkedin-content__customer'>
          <div className='linkedin-content__customer__avatar'>
            {socialData.logo.original ? (
              <img
                src={socialData.logo.original}
                alt='any'
                className='linkedin-content__customer__avatar__img'
              />
            ) : (
              socialData.logo.default
            )}
          </div>
          <div className='linkedin-content__customer__data'>
            <div className='linkedin-content__customer__data__name'>{socialData.name} </div>
            <div className='linkedin-content__customer__data__followers'>15 000 folowers</div>
            <div className='linkedin-content__customer__data__last-seen'>
              {publishDate} &#xB7;
            </div>
          </div>
          <div className='linkedin-content__customer__etc'>
            <Ellipsis />
          </div>
        </div>
        {restrictions.message && (
          <div className='linkedin-content__content'>
            <MessageField
              {...{
                className: 'linkedin-content',
                value: post.message,
                setExtraField,
                editorRaws: post.message_editor_state,
                attachmentIds: {
                  id: attachmentNotToRemove?.id || 0,
                  temporaryId: attachmentNotToRemove?.temporaryId
                },
                allowSpaceAtMentions: false,
                getMentions: getLinkedinMentions,
                getHashes: getHashtags,
                setMention: setMentionToStore,
                refMediaCallbaks,
                postType: post.type,
                isEditable: post.isEditable,
                onChange: onTextChange,
                social: post.social_network,
                link: attachmentNotToRemove?.extra_fields?.link || null,
                maxLength: 3000
              }}
            />
          </div>
        )}
        {restrictions.link && !restrictions.cards && (
          <LinkPreview
            attachment={attachmentNotToRemove}
            isEditable={post.isEditable}
            deleteMediaFromPostSocial={deleteMediaFromPostSocial}
            setExtraField={setExtraField}
            refMediaCallbaks={refMediaCallbaks}
          />
        )}
      </div>
      {restrictions.mediaRestrictions.image && (
        <div className='linkedin-images'>
          <UploadImage {...manageMediaProps} />
        </div>
      )}
      {restrictions.mediaRestrictions.video && (
        <>
          <div className='linkedin-video'>
            <UploadVideo {...{ ...manageMediaProps, deleteMediaFromPostSocial }} />
          </div>
          {!!post.attachments.length && (
            <div className='linkedin-video-title'>
              <VideoTitleInput
                {...{
                  className: 'linkedin-video-title__input',
                  isEditable: post.isEditable,
                  attachmentIds: {
                    id: attachmentNotToRemove?.id || 0,
                    temporaryId: attachmentNotToRemove?.temporaryId
                  },
                  value: attachmentNotToRemove?.extra_fields?.title,
                  social: post.social_network,
                  setExtraField
                }}
              />
            </div>
          )}
        </>
      )}
      <div className='linkedin-assessments'>
        <div className='linkedin-assessments__item'>
          <AssesmentLike />
        </div>
        <div className='linkedin-assessments__item'>
          <AssesmentsClap />
        </div>
        <div className='linkedin-assessments__item'>
          <AssesmentsHeart />
        </div>
        <div className='linkedin-assessments__quantity'>3 000 &#xB7;</div>
        <div className='linkedin-assessments__commments'>78 comments</div>
      </div>
      <div className='linkedin-reactions'>
        <div className='linkedin-reactions__item'>
          <div className='linkedin-reactions__item__icon'>
            <Like />
          </div>
          <div className='linkedin-reactions__item__message'>Like</div>
        </div>
        <div className='linkedin-reactions__item'>
          <div className='linkedin-reactions__item__icon'>
            <Comment />
          </div>
          <div className='linkedin-reactions__item__message'>Comment</div>
        </div>
        <div className='linkedin-reactions__item'>
          <div className='linkedin-reactions__item__icon'>
            <Share />
          </div>
          <div className='linkedin-reactions__item__message'>Share</div>
        </div>
        <div className='linkedin-reactions__item'>
          <div className='linkedin-reactions__item__icon'>
            <Send />
          </div>
          <div className='linkedin-reactions__item__message'>Send</div>
        </div>
      </div>
    </div>
  )
}

export default LinkedinEditor
